import { useCallback, useState } from "react"

import { useQueries, useQueryClient } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"

import { BffApiClientMolecule } from "@/app/api-client/store"
import { CollectionSortEnum } from "@anaconda/core/nft-collection/types"
import type { NftSearchItem } from "@anaconda/core/nft/search/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { CollectionSearchBlockchainMoleculeReatom } from "./collection-search-blockchain-molecule.reatom"

export interface CollectionSearchResult {
  name: string
  image?: string
  id: string
  /** Absent when the collection has no floor on Rarible or Magic Eden. */
  floorPrice?: PriceValue
  /** The golden tick. */
  verified?: boolean
}

const DEFAULT_COLLECTION_QUERY_SIZE = 15
const DEFAULT_NFT_QUERY_SIZE = 15
const HEADER_SEARCH_QUERY_KEY = "header-search"

export const useSearchReatom = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const queryClient = useQueryClient()
  const bffApiClient = useMolecule(BffApiClientMolecule)
  const { blockchainsQueryFilter } = useMolecule(
    CollectionSearchBlockchainMoleculeReatom,
  )
  const blockchains = blockchainsQueryFilter()
  const isSearchEnabled = searchQuery.length > 0

  const [collectionsQuery, nftsQuery] = useQueries({
    queries: [
      {
        queryKey: [
          HEADER_SEARCH_QUERY_KEY,
          "collections",
          searchQuery,
          blockchains,
        ],
        queryFn: async () => {
          const response = await bffApiClient.api.collections.search.$post({
            json: {
              text: searchQuery,
              size: DEFAULT_COLLECTION_QUERY_SIZE,
              blockchains,
              sort: CollectionSortEnum.VOLUME_DESC,
            },
          })

          if (!response.ok) {
            console.error("Collection search error:", response.status)
            return [] as CollectionSearchResult[]
          }

          return (await response.json()) as CollectionSearchResult[]
        },
        enabled: isSearchEnabled,
        staleTime: 0,
      },
      {
        queryKey: [HEADER_SEARCH_QUERY_KEY, "nfts", searchQuery, blockchains],
        queryFn: async () => {
          const response = await bffApiClient.api.nfts["header-search"].$post({
            json: {
              text: searchQuery,
              size: DEFAULT_NFT_QUERY_SIZE,
              blockchains,
            },
          })

          if (!response.ok) {
            console.error("NFT header search error:", response.status)
            return [] as NftSearchItem[]
          }

          return (await response.json()) as NftSearchItem[]
        },
        enabled: isSearchEnabled,
        staleTime: 0,
      },
    ],
  })

  const clearSearchQueries = useCallback(() => {
    void queryClient.cancelQueries({ queryKey: [HEADER_SEARCH_QUERY_KEY] })
    void queryClient.removeQueries({ queryKey: [HEADER_SEARCH_QUERY_KEY] })
  }, [queryClient])

  const search = useCallback(
    (query: string) => {
      if (!query) {
        setSearchQuery("")
        clearSearchQueries()
        return
      }

      setSearchQuery(query)
    },
    [clearSearchQueries],
  )

  const reset = useCallback(() => {
    setSearchQuery("")
    clearSearchQueries()
  }, [clearSearchQueries])

  const isLoadingCollections = isSearchEnabled && collectionsQuery.isFetching
  const isLoadingNfts = isSearchEnabled && nftsQuery.isFetching
  const isLoading = isLoadingCollections || isLoadingNfts

  return {
    collectionResults: isSearchEnabled ? (collectionsQuery.data ?? []) : [],
    nftResults: isSearchEnabled ? (nftsQuery.data ?? []) : [],
    isLoadingCollections,
    isLoadingNfts,
    isLoading,
    isOpen: isSearchEnabled,
    search,
    reset,
  }
}
